'use es6';

import PropTypes from 'prop-types';
import { useState, useCallback, useEffect, useRef } from 'react';
import UIAbstractFloaty from 'UIComponents/tooltip/UIAbstractFloaty';
import TableFloatyPopoverBody from 'rich-text-lib/components/table/TableFloatyPopoverBody';
import withTinyMcePopoverLifecycle from '../hoc/withTinyMcePopoverLifecycle';
import { isResponsiveTableNode } from '../utils/table';
import { jsx as _jsx } from "react/jsx-runtime";
const FloatyPopoverWithLifecycle = withTinyMcePopoverLifecycle(UIAbstractFloaty);
const removeMceSelectedAttr = tableNode => {
  tableNode.parentNode.removeAttribute('data-mce-selected');
};
const propTypes = {
  editor: PropTypes.object.isRequired,
  eventKey: PropTypes.string.isRequired,
  onCloneTable: PropTypes.func.isRequired,
  onDeleteTable: PropTypes.func.isRequired
};
function TablePopoverManagerV2(props) {
  const {
    editor,
    eventKey,
    onCloneTable,
    onDeleteTable
  } = props;
  const [target, setTarget] = useState(null);
  const [refreshKey, setRefreshKey] = useState('');
  const lastTarget = useRef(target);
  useEffect(() => {
    lastTarget.current = target;
  });
  const handleRemove = useCallback(() => {
    const lastTableEl = lastTarget.current;
    if (lastTableEl) {
      if (isResponsiveTableNode(lastTableEl)) {
        removeMceSelectedAttr(lastTableEl);
      }
      setTarget(null);
    }
  }, []);
  useEffect(() => {
    const handleNodeChange = ({
      element
    }) => {
      if (!element) {
        handleRemove();
        return;
      }
      const lastTableEl = lastTarget.current;
      if (lastTableEl !== element) {
        if (lastTableEl && isResponsiveTableNode(lastTableEl)) {
          removeMceSelectedAttr(lastTableEl);
        }
        setTarget(element);
      } else {
        // We need this dummy prop to force a re-render so tether updates
        // after alignment and dimension changes
        const wrapper = isResponsiveTableNode(element) ? element.parentNode : element;
        setRefreshKey(wrapper.outerHTML);
      }
      if (isResponsiveTableNode(element)) {
        element.parentNode.setAttribute('data-mce-selected', '2');
      }
    };
    editor.on(`hsNodeChange:${eventKey}`, handleNodeChange);
    return () => editor.off(`hsNodeChange:${eventKey}`, handleNodeChange);
  }, [editor, eventKey, handleRemove]);
  const handleSelectTable = () => {
    editor.selection.select(target);
  };
  const handleOpenStart = () => {
    // Select table node if not already selected
    if (editor.selection.getNode() !== target) {
      handleSelectTable();
    }
  };
  if (!target) return null;
  return /*#__PURE__*/_jsx(FloatyPopoverWithLifecycle, {
    "data-test-id": "table-selector-popover",
    layerClassName: "mce-hubspot-floaty",
    target: target,
    onRemove: handleRemove,
    editor: editor,
    refreshKey: refreshKey,
    content: /*#__PURE__*/_jsx(TableFloatyPopoverBody, {
      className: "mce-table-popover",
      dropdownClassName: "mce-table-dropdown",
      onOpenStart: handleOpenStart,
      onSelectTable: handleSelectTable,
      onCloneTable: onCloneTable.bind(null, target),
      onDeleteTable: onDeleteTable.bind(null, target)
    }),
    computePosition: ({
      targetBounds,
      contentDimensions
    }) => {
      // Default to 26px height, the realized height of the floaty content
      const contentHeight = contentDimensions.height || 26;
      return {
        top: targetBounds.top - contentHeight,
        left: targetBounds.left
      };
    }
  });
}
TablePopoverManagerV2.propTypes = propTypes;
export default TablePopoverManagerV2;